import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import ActiveAgentIcon from "../../components/ActiveAgentIcon";
import { useRunningConversations } from "../../llm/runningConversations";
import { useConversationStreamClient } from "./ConversationStreamProvider";

const toMs = (v) => {
  if (v == null) return NaN;
  return typeof v === "number" ? v : new Date(v).getTime();
};

const formatRelative = (iso) => {
  const ts = toMs(iso);
  if (Number.isNaN(ts)) return "—";
  const delta = Date.now() - ts;
  const min = Math.floor(delta / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d`;
  return new Date(ts).toLocaleDateString();
};

// PLEX-3975: exact date+time for the timestamp shown next to each conversation.
// The compact relative label ("2h") stays as the visible text; the absolute
// value is exposed via the <time title>/dateTime so users can see precisely
// when a workflow last changed (hover / assistive tech / semantic markup).
export const formatAbsolute = (iso) => {
  const ts = toMs(iso);
  if (Number.isNaN(ts)) return "";
  return new Date(ts).toLocaleString();
};

export const toISO = (iso) => {
  const ts = toMs(iso);
  return Number.isNaN(ts) ? undefined : new Date(ts).toISOString();
};

export const SORTS = {
  recent: { label: "Recent", compare: null }, // mergeChats already returns newest-first
  topic: {
    label: "Topic",
    compare: (a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" }),
  },
};

// Apply a sort mode to an already-merged (newest-first) chat list. "recent" is a
// pass-through; unknown modes fall back to the input order. Pure — unit-tested.
export const sortChats = (chats = [], sortBy = "recent") => {
  const compare = SORTS[sortBy]?.compare;
  return compare ? [...chats].sort(compare) : (chats || []);
};

/**
 * Match a single history item against a v2 program/project. Used by both
 * the in-rail merge and the workspace/program-home Recent-chats sidebars
 * (which derive their list from historyStore rather than the fixture).
 *
 * v2 conversations are persisted with one or more of these markers:
 *   - context.projectPath  === "/v2/p/<program>/<project>"
 *   - context.v2.programId === <program> && context.v2.projectId === <project>
 *   - context.url includes  "/v2/p/<program>/<project>"
 *
 * `programId` / `projectId` parameters are optional — when omitted we
 * still require the conversation to be v2-tagged (any of the above
 * markers present), but allow any program/project.
 */
// True iff `url`'s path segment equals `prefix` or `prefix` is followed by a
// path delimiter ("/" or "?"). Plain `url.includes(prefix)` will substring-
// match similarly-named siblings — e.g. project "test" would match the URL
// "/v2/p/kras/test-2/c/abc" — so use this helper everywhere we want a
// project-path comparison.
const urlMatchesProjectPath = (url, prefix) => {
  if (typeof url !== "string" || !prefix) return false;
  const idx = url.indexOf(prefix);
  if (idx === -1) return false;
  const next = url.charAt(idx + prefix.length);
  return next === "" || next === "/" || next === "?";
};

export const matchesV2Project = (h, programId = null, projectId = null) => {
  const ctx = h?.context;
  if (!ctx) return false;
  const v2 = ctx.v2;
  if (programId && projectId) {
    const urlPrefix = `/v2/p/${programId}/${projectId}`;
    if (ctx.projectPath === urlPrefix) return true;
    if (v2?.programId === programId && v2?.projectId === projectId) return true;
    return urlMatchesProjectPath(ctx.url, urlPrefix);
  }
  if (programId && !projectId) {
    const programPrefix = `/v2/p/${programId}`;
    if (typeof ctx.projectPath === "string" && ctx.projectPath.startsWith(`${programPrefix}/`)) return true;
    if (v2?.programId === programId) return true;
    return urlMatchesProjectPath(ctx.url, programPrefix);
  }
  // No filter — any v2 marker counts.
  if (typeof ctx.projectPath === "string" && ctx.projectPath.startsWith("/v2/p/")) return true;
  if (v2?.programId && v2?.projectId) return true;
  return typeof ctx.url === "string" && ctx.url.includes("/v2/p/");
};

/**
 * Pull v2-tagged conversations out of historyStore, optionally scoped to
 * a program (and/or project). Returns the recent-chats card shape the
 * workspace + program home sidebars expect. Newest-first by lastModified.
 */
export const recentV2Conversations = (historyItems = [], { programId = null, projectId = null, limit = 8 } = {}) => {
  const matched = (historyItems || []).filter((h) => matchesV2Project(h, programId, projectId));
  const shaped = matched.map((h) => {
    const ctx = h?.context || {};
    const v2 = ctx.v2 || {};
    const projPath = (ctx.projectPath || "").match(/^\/v2\/p\/([^/]+)\/([^/]+)/);
    const pid = v2.programId || (projPath && projPath[1]) || null;
    const projId = v2.projectId || (projPath && projPath[2]) || null;
    return {
      id: h.id,
      programId: pid,
      projectId: projId,
      title: ctx.title || "Untitled chat",
      snippet: ctx.snippet || "",
      lastActiveAt: h.modified || h.created || ctx.savedAt || null,
    };
  });
  shaped.sort((a, b) => {
    const at = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
    const bt = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
    return bt - at;
  });
  return limit ? shaped.slice(0, limit) : shaped;
};

/**
 * Count v2 conversations under a project. Used by ProjectCard so the
 * "N chats" label reflects real activity (backend stores a static
 * `chatCount` field that never gets recomputed).
 */
export const countProjectChats = (historyItems = [], programId, projectId) =>
  (historyItems || []).filter((h) => matchesV2Project(h, programId, projectId)).length;

/**
 * Merge fixture/local chats with live history-store conversations whose
 * persisted URL falls under this v2 project. Local-fixture entries take
 * priority on id collisions so optimistic UI doesn't flicker. Sorted
 * newest-first.
 */
export const mergeChats = (fixtureChats = [], historyItems = [], programId, projectId) => {
  const urlPrefix = `/v2/p/${programId}/${projectId}`;
  const matchesProject = (h) => {
    // Primary: v2 persistence writes `context.projectPath = /v2/p/<prog>/<proj>`.
    if (h?.context?.projectPath === urlPrefix) return true;
    // Secondary: a v2.programId/projectId fingerprint, in case projectPath is missing.
    if (h?.context?.v2?.programId === programId && h?.context?.v2?.projectId === projectId) return true;
    // Legacy fallback: search-type items whose context.url falls under the
    // project path. Use the boundary-aware helper — plain `includes` would
    // false-match similarly-named projects (e.g. "test" matching "test-2").
    return urlMatchesProjectPath(h?.context?.url, urlPrefix);
  };
  const fromHistory = (historyItems || [])
    .filter(matchesProject)
    .map((h) => ({
      id: h.id,
      title: h.context?.title || "Untitled chat",
      snippet: h.context?.snippet || "",
      lastActiveAt: h.modified || h.created || h.context?.savedAt || null,
      fromHistory: true,
    }));

  const seen = new Set();
  const merged = [];
  (fixtureChats || []).forEach((c) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    merged.push(c);
  });
  fromHistory.forEach((c) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    merged.push(c);
  });

  return merged.sort((a, b) => {
    const at = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
    const bt = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
    return bt - at;
  });
};

export const ChatListItem = ({ chat, programId, projectId, isActive, running = false, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(chat.title || "Untitled chat");

  // Keep local input in sync when the parent reflows the chat (e.g. another
  // item's rename caused the list to re-render).
  useEffect(() => { setTitle(chat.title || "Untitled chat"); }, [chat.title]);

  const startEdit = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setEditing(true);
  };
  const cancelEdit = () => {
    setTitle(chat.title || "Untitled chat");
    setEditing(false);
  };
  const saveEdit = () => {
    const next = (title || "").trim();
    if (next && next !== chat.title) onRename?.(chat.id, next);
    setEditing(false);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== "undefined" && window.confirm(`Delete chat "${chat.title}"?`)) {
      onDelete?.(chat.id);
    }
  };

  const itemClassName =
    `chatlist__item${isActive ? " chatlist__item--active" : ""}` +
    `${chat.fromHistory ? " chatlist__item--live" : ""}` +
    `${editing ? " chatlist__item--edit-mode" : ""}`;

  if (editing) {
    return (
      <li
        className={itemClassName}
        data-testid={`v2-chatlist-item-${chat.id}`}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
          if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
        }}
      >
        <input
          autoFocus
          className="chatlist__title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Chat title"
          data-testid={`v2-chatlist-rename-input-${chat.id}`}
        />
        <footer className="chatlist__footer chatlist__footer--editing">
          <button
            type="button"
            className="chatlist__cancel"
            onClick={cancelEdit}
            data-testid={`v2-chatlist-cancel-${chat.id}`}
          >
            Cancel
          </button>
          <button
            type="button"
            className="chatlist__save"
            onClick={saveEdit}
            disabled={!(title || "").trim()}
            data-testid={`v2-chatlist-save-${chat.id}`}
          >
            Save
          </button>
        </footer>
      </li>
    );
  }

  return (
    <li className={itemClassName}>
      <Link
        to={`/v2/p/${programId}/${projectId}/c/${chat.id}`}
        className="chatlist__link"
        data-testid={`v2-chatlist-item-${chat.id}`}
        aria-label={`Open ${chat.title}`}
      >
        <div
          className="chatlist__title"
          data-testid={`v2-chatlist-title-${chat.id}`}
        >
          {running && <ActiveAgentIcon />}
          {chat.title}
        </div>
        {chat.snippet && (
          <div className="chatlist__snippet">{chat.snippet}</div>
        )}
        <time
          className="chatlist__time"
          dateTime={toISO(chat.lastActiveAt)}
          title={formatAbsolute(chat.lastActiveAt)}
          data-testid={`v2-chatlist-time-${chat.id}`}
        >
          {formatRelative(chat.lastActiveAt)}
          {chat.fromHistory && <span className="chatlist__live"> · LIVE</span>}
        </time>
      </Link>
      <footer className="chatlist__footer">
        <button
          type="button"
          className="chatlist__edit"
          onClick={startEdit}
          title="Rename chat"
          data-testid={`v2-chatlist-edit-${chat.id}`}
        >
          ✎
        </button>
        <button
          type="button"
          className="chatlist__delete"
          onClick={handleDelete}
          title="Delete chat"
          data-testid={`v2-chatlist-delete-${chat.id}`}
        >
          ✕
        </button>
      </footer>
    </li>
  );
};

const ChatListRailBase = ({
  workspaceStore,
  historyStore,
  userStore,
  programId,
  projectId,
  currentChatId,
  onNewChat,
  onCollapse,
}) => {
  const navigate = useNavigate();
  const authReady = !!userStore?.authData;
  const [sortBy, setSortBy] = useState("recent"); // PLEX-3975: "recent" | "topic"

  useEffect(() => {
    if (!programId || !projectId || !authReady) return;
    if (workspaceStore.getChatsForProject(programId, projectId).length === 0) {
      workspaceStore.loadChatsForProject(programId, projectId);
    }
    // Pull live history once so v2-tagged conversations surface in the rail.
    if (historyStore && (!historyStore.conversations || historyStore.conversations.length === 0)) {
      historyStore.loadHistory?.("CONVERSATION").catch(() => {/* ignore — fixture data still renders */});
    }
  }, [programId, projectId, workspaceStore, historyStore, authReady]);

  // PLEX-6361: reload on each chat switch too, so a chat left while its agent works shows as
  // running (the backend marks its record once the turn starts).
  const reloadHistory = useCallback(() => {
    historyStore?.loadHistory?.("CONVERSATION").catch(() => {/* ignore — the cached list still renders */});
  }, [historyStore]);
  useEffect(() => {
    if (authReady) reloadHistory();
  }, [currentChatId, authReady, reloadHistory]);

  const fixtureChats = workspaceStore.getChatsForProject(programId, projectId);
  const historyItems = historyStore?.conversations || [];
  // PLEX-6361: chats with an agent still working: the history records the backend marks in
  // progress whose turn still runs, and the chat whose turn this view is streaming.
  const running = useRunningConversations(historyItems, { auth: userStore?.authData, onEnded: reloadHistory });
  const client = useConversationStreamClient();
  const liveChatId = (client?.streamId || client?.waitingForResponse || client?.turnRunning)
    ? client.historyId : null;
  const chats = useMemo(
    () => mergeChats(fixtureChats, historyItems, programId, projectId),
    [fixtureChats, historyItems, programId, projectId],
  );

  // PLEX-3975: user-selectable order. mergeChats already returns newest-first,
  // so "recent" is a pass-through; "topic" sorts alphabetically by title.
  const sortedChats = useMemo(() => sortChats(chats, sortBy), [chats, sortBy]);

  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
      return;
    }
    navigate(`/v2/p/${programId}/${projectId}`);
  };

  return (
    <aside className="chatlist stone-card--subtle" data-testid="v2-chatlist">
      <header className="chatlist__header">
        <button
          type="button"
          className="chatlist__new"
          onClick={handleNewChat}
          data-testid="v2-chatlist-new"
        >
          + New chat
        </button>
        {onCollapse && (
          <button
            type="button"
            className="chatlist__collapse"
            onClick={onCollapse}
            title="Hide chat list"
            aria-label="Hide chat list"
            data-testid="v2-chatlist-collapse"
          >
            <span aria-hidden="true">‹</span>
          </button>
        )}
      </header>

      {chats.length > 0 && (
        <div className="chatlist__toolbar" data-testid="v2-chatlist-toolbar">
          <label className="chatlist__sort-label" htmlFor="chatlist-sort">Sort</label>
          <select
            id="chatlist-sort"
            className="chatlist__sort"
            data-testid="v2-chatlist-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort conversations"
          >
            {Object.entries(SORTS).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      )}

      {chats.length === 0 ? (
        <div className="chatlist__empty">No chats yet. Start one with a question below.</div>
      ) : (
        <ul className="chatlist__items" data-testid="v2-chatlist-items">
          {sortedChats.map((chat) => (
            <ChatListItem
              key={chat.id}
              chat={chat}
              programId={programId}
              projectId={projectId}
              isActive={chat.id === currentChatId}
              running={running.has(chat.id) || (!!liveChatId && chat.id === liveChatId)}
              onRename={(id, title) => {
                // Look up the live history record so we preserve all
                // existing context markers (projectPath, v2.programId/
                // projectId, cell, version, authors). The merged-list
                // shape we render with strips those, and the PUT
                // endpoint replaces context wholesale — sending the
                // shallow shape would un-tag the chat from its project.
                const existing = historyItems.find((h) => h.id === id);
                const baseContext = existing?.context || {
                  // Synthesize project markers when the chat is local-
                  // only (optimistic fixture entry that hasn't round-
                  // tripped yet).
                  projectPath: `/v2/p/${programId}/${projectId}`,
                  v2: { programId, projectId, conversationId: id },
                };
                return historyStore
                  ?.updateConversation?.(id, { context: { ...baseContext, title } })
                  .catch((e) => console.error(e));
              }}
              onDelete={(id) => historyStore?.deleteItem?.(id, "CONVERSATION").catch((e) => console.error(e))}
            />
          ))}
        </ul>
      )}
    </aside>
  );
};

const ChatListRail = inject("workspaceStore", "historyStore", "userStore")(observer(ChatListRailBase));
export default ChatListRail;
